/**
 * Recarga la pestaña cuando un service worker NUEVO toma el control.
 *
 * `registerType: 'autoUpdate'` (vite.config.ts) hace que el SW nuevo se instale
 * y se active solo — `skipWaiting` + `clientsClaim` —, pero eso NO refresca la
 * pestaña que ya está abierta: el usuario sigue viendo el bundle anterior hasta
 * que recargue a mano. El `registerSW.js` que inyecta el plugin solo registra el
 * SW; no tiene ninguna lógica de refresco.
 *
 * Incidente 2026-08-19: tras el release #1026 —desplegado y correcto— un cliente
 * seguía viendo la UI de antes del 10-07, con el texto de una pantalla que ese
 * release ya no contiene. El servidor servía el bundle nuevo; la pestaña corría
 * el viejo desde el precache del SW.
 *
 * ⚠️ El guard de `controller` es lo que evita un loop de recargas: en la PRIMERA
 * visita no hay SW controlando la página, y cuando el recién instalado llama a
 * `clientsClaim()` dispara `controllerchange` igual. Sin el guard, esa primera
 * carga se recargaría sola sin que haya ninguna versión vieja que reemplazar.
 */
/**
 * Rutas donde una recarga ROMPE el flujo en curso en vez de mejorarlo.
 *
 * 🔴 Incidente 2026-08-25, el día del release #1045: nadie con la app abierta
 * podía entrar. El backend autenticaba bien —`POST /auth/token/` devolvía 200 y
 * el callback de Google registraba «Login SSO exitoso»— pero el usuario volvía
 * al login con un error.
 *
 * La causa era este mismo auto-reload disparando en medio del login:
 *
 *   1. el frontend consume el token y recibe los JWT              200
 *   2. el SW nuevo toma el control  →  controllerchange
 *   3. recarga  →  el accessToken vive SOLO EN MEMORIA y se pierde
 *   4. POST /auth/token/refresh/                                  401
 *   5. de vuelta al login
 *
 * En el SSO es peor: el token OTP de Google es de **un solo uso**
 * (`cache.delete(cache_key)` en `views_auth.py`), así que el reintento tras la
 * recarga encuentra un token ya quemado y falla para siempre.
 *
 * Postergar la recarga no pierde nada: el SW nuevo ya está activo y la próxima
 * navegación sirve el bundle nuevo igual.
 */
const RUTAS_SIN_RECARGA = ['/auth/', '/login', '/reset-password']

function hayFlujoDeAutenticacionEnCurso(): boolean {
  if (typeof window === 'undefined') return false
  const { pathname, search } = window.location
  if (RUTAS_SIN_RECARGA.some((r) => pathname.includes(r))) return true
  // El callback de SSO llega con el token de un solo uso en la query.
  return /[?&](token|code|state)=/.test(search)
}

/**
 * ¿La pestaña corre un bundle distinto del que sirve hoy el servidor?
 *
 * 🔴 Existe por un reporte de Gabriel (2026-10-15): al cambiar de empresa, la
 * pantalla mostraba los datos de la nueva y a los pocos segundos se recargaba
 * sola. Cada empresa es otro subdominio, o sea otro ORIGEN con su propio SW. Si
 * ese subdominio tenía el SW de un deploy anterior, la navegación —red primero
 * desde el #1027— ya traía el bundle nuevo, pero el SW nuevo se instalaba
 * después, tomaba el control y disparaba la recarga igual: una recarga que no
 * reemplazaba nada. Con deploys diarios en staging pasaba en cada cambio.
 *
 * Compara el script de entrada (`/assets/index-<hash>.js`, el hash cambia con
 * cada build) de la pestaña contra el del `index.html` que sirve el servidor.
 * Si no lo puede determinar, responde `true`: ante la duda manda el incidente
 * del 2026-08-19 —un cliente atrapado en un bundle viejo es peor que una
 * recarga de más—.
 */
export async function bundleDesactualizado(): Promise<boolean> {
  try {
    const actual = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src
    if (!actual) return true
    const res = await fetch('/', { cache: 'no-store', credentials: 'same-origin' })
    if (!res.ok) return true
    const html = await res.text()
    const servido = html.match(/<script[^>]*type="module"[^>]*src="([^"]+)"/)?.[1]
    if (!servido) return true
    return new URL(servido, window.location.origin).pathname !== new URL(actual).pathname
  } catch {
    return true
  }
}

export function setupServiceWorkerAutoReload(
  reload: () => void = () => window.location.reload(),
  estaDesactualizado: () => Promise<boolean> = bundleDesactualizado,
): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

  // Sin controller no hay versión previa que reemplazar: es una instalación
  // nueva, no una actualización.
  if (!navigator.serviceWorker.controller) return

  let revisando = false
  navigator.serviceWorker.addEventListener('controllerchange', async () => {
    if (revisando) return
    // ⚠️ NO recargar durante un login: ver el comentario de arriba. El bundle
    // nuevo entra igual en la siguiente navegación.
    if (hayFlujoDeAutenticacionEnCurso()) return
    revisando = true
    // Un SW nuevo no implica que la pestaña sea vieja: si la navegación ya trajo
    // el bundle de este deploy, recargar solo interrumpe al usuario.
    if (await estaDesactualizado()) reload()
  })
}
